import IUser from '../app/authentication/~store/models/User';
import { IApiActionResult, IErrorObj } from './ApiActionResult';
import i18n from "src/app/shared/localization/i18n";
import { showErrorToastr } from 'src/app/shared/helpers/toastr-helper/ToastrHelper';
import { redirectToLogin } from 'src/~store/history';

export const fetchGet = 
    async (requestUrl: string, requestOptions?: RequestInit, withCredentials: boolean = false): Promise<Response> => {
        const authHeader = getAuthHeader();

        let headers: Headers = new Headers();
        headers.set('Content-Type', 'application/json');
        if(authHeader !== null){
            headers.set('Authorization', authHeader.Authorization);
        }
        const request = {
            ...requestOptions,
            method: 'GET',
            headers: headers
        };

        return await fetch(requestUrl, request);
}

export const fetchPost =
    async (requestUrl: string, requestOptions?: RequestInit): Promise<Response> => {
        const authHeader = getAuthHeader();

        let headers: Headers = new Headers();
        let body = requestOptions?.body;
        if(!(body instanceof FormData)){
            headers.set('Content-Type', 'application/json');
        }
        if(authHeader !== null){
            headers.set('Authorization', authHeader.Authorization);
        }

        return await fetch(requestUrl, {
            ...requestOptions,
            method: 'POST',
            headers: headers
        })
}

export const getApiActionResult = 
    async <T>(
        fetchPromise: Promise<Response>, 
        hasReturnValue: boolean = false
    ): Promise<IApiActionResult<T>> => {
        let statusCode: number = 0;
        let tryShowErrorToastr = (): void => {};
        try {
            const response: Response = await fetchPromise;
            statusCode = response.status;
            if(response.ok) {
                let value: any = ""; 
                if(response.status !== 204) {
                    value = hasReturnValue ? await response.json() : null;
                }
                return { isSuccess: true, value, statusCode, tryShowErrorToastr };
            } else {
                let errorText: string | undefined = undefined;
                let errorObj: IErrorObj | undefined = undefined;
                try {
                    errorText = await response.text();
                    if (response.status === 401) {
                        let invalidToken = i18n.t(errorText ?? "error.invalidTokenErrorMessage");
                        redirectToLogin(`${invalidToken}`);
                    }
                    if (response.status === 413) {
                        let fileTooLarge = i18n.t("error.fileTooLargeErrorMessage");
                        showErrorToastr(`${fileTooLarge}`);
                    }
                    errorObj = JSON.parse(errorText ?? "null")
                    tryShowErrorToastr = () => {
                        if(errorObj) {
                            showErrorToastr(i18n.t(errorObj.errorCode, errorObj.data));
                        }
                        else {
                            showErrorToastr(i18n.t('error.error'));
                        }
                    }
                } catch {
                    errorText = undefined;
                }                
                return { isSuccess: false, statusCode, errorText, tryShowErrorToastr }
            }
        } catch(ex) {
            return { isSuccess: false, statusCode, tryShowErrorToastr };
        }
}

export const fetchGetFile = async (
    requestUrl: string, 
    filename: string, 
    requestOptions?: RequestInit
): Promise<any> => {
    const authUserHeaders = getAuthHeader();
    const method = "GET";
    return await fetch(requestUrl, {
        ...requestOptions,
        method: method,
        headers: {
            "Content-Type": "application/json",
            ...authUserHeaders
        }
    })
        .then(async response => {
            if (response.status === 200) 
                await response.blob()
                    .then(blob => {
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = filename;
                        a.click();
                    });
            return response;
        });
};

export const fetchPostFile = async (
    requestUrl: string, 
    filename: string, 
    requestOptions?: RequestInit
): Promise<any> => {
    const authUserHeaders = getAuthHeader();
    const method = "POST";
    return await fetch(requestUrl, {
        ...requestOptions,
        method: method,
        headers: {
            "Content-Type": "application/json",
            ...authUserHeaders
        }
    })
        .then(async response => {
            if (response.status === 200) 
                await response.blob()
                    .then(blob => {
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = filename;
                        a.click();
                    });
            return response;
        });
};

export const getBase64FromFile = (file: File): Promise<string> => {
    return new Promise<string> ((resolve,reject)=> {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result?.toString().replace(/^data:(.*,)?/, '') || '');
        reader.onerror = error => reject(error);
    })
}

const getAuthHeader = (): Record<'Authorization', string> | null => {
    try {
        const user: IUser = JSON.parse(sessionStorage.getItem('user') ?? "");
        if(user.isAuth &&
           user.tokenInfo.token.length > 0) {
            return {'Authorization': 'Bearer ' + user.tokenInfo.token };    
        } else {
            return null;
        }
    } catch(ex) {
        return null;
    }
}

export const getApiFileResult =
    async (fetchPromise: Promise<Response>): Promise<IApiActionResult<Blob>> => {
        try {
            const response = await fetchPromise;

            if (response.ok) {
                const value = await response.blob();
                return { isSuccess: true, value, statusCode: response.status, tryShowErrorToastr: () => { } };
            } else {
                return getApiActionResult(Promise.resolve(response)) as any;
            }
        } catch (ex) {
            return getApiActionResult(Promise.reject(ex)) as any;
        }
    };

export const fetchDelete =
    async (requestUrl: string, requestOptions?: RequestInit): Promise<Response> => {
        const authHeader = getAuthHeader();

        let headers: Headers = new Headers();
        let body = requestOptions?.body;
        if (!(body instanceof FormData)) {
            headers.set('Content-Type', 'application/json');
        }
        if (authHeader !== null) {
            headers.set('Authorization', authHeader.Authorization);
        }

        return await fetch(requestUrl, {
            ...requestOptions,
            method: 'Delete',
            headers: headers
        })
    }